import { lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "../lib/api";
import type { GraphPayload, PurityRow } from "../lib/api";
import { intBR, pct } from "../lib/format";
import { Card, ChartFrame, ErrorState, PageHead, Skeleton } from "../components/ui";
import { useActiveUser } from "../components/Shell";

// Cytoscape só carrega aqui: as outras oito rotas não pagam os ~140 KB.
const KnowledgeGraph = lazy(() =>
  import("../charts/KnowledgeGraph").then((m) => ({ default: m.KnowledgeGraph })));

const TYPES = [
  ["empresas", "empresa"], ["setor", "setor"], ["pessoas", "pessoa"],
  ["cidade", "cidade"], ["canal", "canal"], ["gateway", "gateway"],
  ["produto", "produto"], ["uf", "uf"], ["bairro", "bairro"],
  ["orgao_publico", "órgão público"], ["ticker", "ticker"],
];

export function Graph() {
  const userId = useActiveUser();
  const [params, setParams] = useSearchParams();
  const top = Number(params.get("top") || 30);
  const minWeight = Number(params.get("peso") || 0);
  const types = params.get("tipos") || "";
  const withCategories = params.get("categorias") !== "0";
  const withIsolated = params.get("soltos") === "1";

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  const selected = new Set(types ? types.split(",") : []);
  const toggleType = (key: string) => {
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    set("tipos", [...next].join(","));
  };

  const graph = useQuery({
    queryKey: ["graph", userId, top, minWeight, types, withCategories, withIsolated],
    queryFn: () => api<GraphPayload>(
      `/api/ontology/users/${userId}/graph${qs({
        top, min_weight: minWeight, types,
        categories: withCategories ? undefined : "0",
        isolated: withIsolated ? "1" : undefined })}`),
    enabled: !!userId,
    placeholderData: (previous) => previous,
  });
  const purity = useQuery({
    queryKey: ["purity"],
    queryFn: () => api<PurityRow[]>("/api/ontology/purity"),
  });

  return (
    <>
      <PageHead eyebrow={`grafo · ${userId?.slice(0, 8) ?? ""}`} title="Grafo de entidades">
        Duas entidades se ligam quando aparecem na mesma descrição. A espessura da aresta é
        a massa que passou por essa ligação.
      </PageHead>

      <div className="controls">
        <label>nós
          <select value={top} onChange={(e) => set("top", e.target.value)}>
            {[30, 60, 110, 250, 500].map((n) => (
              <option key={n} value={n}>{n} maiores</option>
            ))}
          </select>
        </label>
        <label>peso mínimo
          <select value={minWeight} onChange={(e) => set("peso", e.target.value)}>
            <option value="0">todas as ligações</option>
            <option value="1000">R$ 1 mil+</option>
            <option value="10000">R$ 10 mil+</option>
            <option value="100000">R$ 100 mil+</option>
          </select>
        </label>
        <label className="switch">
          <input type="checkbox" checked={withCategories}
                 onChange={(e) => set("categorias", e.target.checked ? "" : "0")} />
          mostrar categorias
        </label>
        <label className="switch">
          <input type="checkbox" checked={withIsolated}
                 onChange={(e) => set("soltos", e.target.checked ? "1" : "")} />
          mostrar nós sem ligação
        </label>
        <div className="chips" role="group" aria-label="Filtrar tipos de entidade">
          {TYPES.map(([key, label]) => (
            <button key={key} className="chip"
                    aria-pressed={selected.size === 0 || selected.has(key)}
                    onClick={() => toggleType(key)}>{label}</button>
          ))}
          {selected.size > 0 && (
            <button className="chip" onClick={() => set("tipos", "")}>limpar</button>
          )}
        </div>
      </div>

      <ChartFrame
        title="Grafo de conhecimento"
        sub={graph.data
          ? `${intBR(graph.data.shown_nodes)} de ${intBR(graph.data.total_nodes)} entidades`
            + (graph.data.category_nodes
                ? ` · ${intBR(graph.data.category_nodes)} categorias` : "")
            + ` · ${intBR(graph.data.edges.length)} ligações`
          : undefined}
        guide="modelo"
        tableHeader={["nó", "papel", "tipo", "linhas", "massa", "categoria dominante"]}
        tableRows={(graph.data?.nodes ?? []).map((n) =>
          [n.name, n.kind, n.label, n.rows, n.amount, n.top_category ?? "—"])}
      >
        {graph.isPending && <Skeleton rows={1} height={560} />}
        {graph.error && <ErrorState error={graph.error} onRetry={() => graph.refetch()} />}
        {graph.data && (
          <Suspense fallback={<Skeleton rows={1} height={560} />}>
            <KnowledgeGraph data={graph.data} />
          </Suspense>
        )}
      </ChartFrame>

      <div style={{ marginTop: 18 }}>
        <Card title="Nem toda entidade serve para classificar"
              sub="pureza mediana por tipo: quanto do volume de uma entidade cai na categoria mais frequente">
          {purity.isPending && <Skeleton rows={11} height={16} />}
          {purity.data && (
            <table>
              <thead>
                <tr>
                  <th scope="col">tipo</th>
                  <th scope="col" className="num">entidades</th>
                  <th scope="col" className="num">pureza mediana</th>
                  <th scope="col" className="num">categorias</th>
                  <th scope="col">serve para</th>
                </tr>
              </thead>
              <tbody>
                {purity.data.map((row) => (
                  <tr key={row.type_key}>
                    <td>{row.label}</td>
                    <td className="num">{intBR(row.entities)}</td>
                    <td className="num">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 54, height: 8, background: "var(--surface-2)",
                                       borderRadius: 2, overflow: "hidden" }}>
                          <span style={{ display: "block", height: "100%",
                                         width: `${(row.median_purity ?? 0) * 100}%`,
                                         background: "var(--accent)" }} />
                        </span>
                        {pct(row.median_purity, 0)}
                      </span>
                    </td>
                    <td className="num">{row.median_categories}</td>
                    <td>
                      <span className="tag">
                        {row.band === "identidade" ? "identidade — diz o quê" : "contexto — diz como ou onde"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
